import { useEffect, useState, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Edit, Trash2, Shield, Eye, EyeOff, Pencil, Key, AlertTriangle, Layers, Search, Zap, ShoppingCart, UtensilsCrossed, Building2, Calculator, Users as UsersIcon, Package, BookOpen, Settings2, Star, Briefcase, Wrench, HeartPulse, Truck, Monitor, Coffee, Scissors, Camera, Palette, Headphones, Flame, TrendingUp, Activity } from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore, useAuthStore } from '@/stores';
import { toast } from 'sonner';
import { BranchFilter } from '@/components/BranchFilter';
import { useLanguage } from '@/contexts/LanguageContext';
import { TemplateManagerDialog } from './users/TemplateManagerDialog';
import { UserAccessSummaryDialog } from './users/UserAccessSummaryDialog';
import UserReportPreferences from '@/components/UserReportPreferences';
import ActivityFeedPanel from '@/components/ActivityFeedPanel';

const APP_MODULES = [
  { id: 'accounting', label: 'Accounting', color: 'bg-info/20 text-info border-info/30' },
  { id: 'hr', label: 'HR & Workforce', color: 'bg-success/20 text-success border-success/30' },
  { id: 'pos', label: 'POS & Sales', color: 'bg-warning/20 text-warning border-warning/30' },
  { id: 'reports', label: 'Reports & Analytics', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  { id: 'admin', label: 'Administration', color: 'bg-muted text-foreground border-border' },
];

// All modules that can have permissions
const ALL_MODULES = [
  { value: 'dashboard', label: 'Dashboard', group: 'Core' },
  { value: 'sales', label: 'Sales', group: 'Core' },
  { value: 'invoices', label: 'Invoices', group: 'Core' },
  { value: 'branches', label: 'Branches', group: 'Core' },
  { value: 'customers', label: 'Customers', group: 'Core' },
  { value: 'suppliers', label: 'Suppliers', group: 'Finance' },
  { value: 'supplier_payments', label: 'Supplier Payments', group: 'Finance' },
  { value: 'expenses', label: 'Expenses', group: 'Finance' },
  { value: 'cash_transfers', label: 'Cash Transfers', group: 'Finance' },
  { value: 'employees', label: 'Employees', group: 'HR' },
  { value: 'documents', label: 'Documents', group: 'HR' },
  { value: 'leave', label: 'Leave Approvals', group: 'HR' },
  { value: 'loans', label: 'Loans', group: 'HR' },
  { value: 'shifts', label: 'Shifts & Schedule', group: 'HR' },
  { value: 'stock', label: 'Inventory', group: 'Stock' },
  { value: 'kitchen', label: 'Kitchen', group: 'Stock' },
  { value: 'pos', label: 'POS', group: 'Operations' },
  { value: 'reports', label: 'Reports', group: 'Reports' },
  { value: 'credit_report', label: 'Credit Report', group: 'Reports' },
  { value: 'supplier_report', label: 'Supplier Report', group: 'Reports' },
  { value: 'analytics', label: 'Analytics', group: 'Reports' },
  { value: 'settings', label: 'Settings', group: 'Admin' },
  { value: 'users', label: 'User Management', group: 'Admin' },
  { value: 'partners', label: 'Partners', group: 'Admin' },
  { value: 'fines', label: 'Fines', group: 'Admin' },
];

const PERMISSION_GROUPS = [...new Set(ALL_MODULES.map(m => m.group))];

// Permission level colors
const PERMISSION_COLORS = {
  write: 'bg-success/20 text-success border-success/30',
  read: 'bg-info/20 text-info border-info/30',
  none: 'bg-muted text-muted-foreground border-border',
};

// Pre-built Role Templates
const ROLE_TEMPLATES = [
  {
    id: 'cashier',
    label: 'Cashier',
    icon: ShoppingCart,
    color: 'from-amber-500 to-orange-600',
    bg: 'bg-warning/10 border-warning/30 hover:border-amber-400',
    description: 'POS & sales transactions',
    role: 'operator',
    allowed_modules: ['pos'],
    permissions: { dashboard: 'none', sales: 'write', invoices: 'read', branches: 'none', customers: 'write', suppliers: 'none', supplier_payments: 'none', expenses: 'none', cash_transfers: 'none', employees: 'none', documents: 'none', leave: 'none', loans: 'none', shifts: 'none', stock: 'read', kitchen: 'none', pos: 'write', reports: 'none', credit_report: 'none', supplier_report: 'none', analytics: 'none', settings: 'none', users: 'none', partners: 'none', fines: 'none' },
  },
  {
    id: 'waiter',
    label: 'Waiter',
    icon: UtensilsCrossed,
    color: 'from-teal-500 to-emerald-600',
    bg: 'bg-teal-50 border-teal-200 hover:border-teal-400',
    description: 'Tables & order taking',
    role: 'operator',
    allowed_modules: ['pos'],
    permissions: { dashboard: 'none', sales: 'write', invoices: 'none', branches: 'none', customers: 'read', suppliers: 'none', supplier_payments: 'none', expenses: 'none', cash_transfers: 'none', employees: 'none', documents: 'none', leave: 'none', loans: 'none', shifts: 'read', stock: 'none', kitchen: 'read', pos: 'write', reports: 'none', credit_report: 'none', supplier_report: 'none', analytics: 'none', settings: 'none', users: 'none', partners: 'none', fines: 'none' },
  },
  {
    id: 'branch_manager',
    label: 'Branch Manager',
    icon: Building2,
    color: 'from-blue-500 to-indigo-600',
    bg: 'bg-info/10 border-info/30 hover:border-blue-400',
    description: 'Full branch operations',
    role: 'manager',
    allowed_modules: ['pos', 'hr', 'reports'],
    permissions: { dashboard: 'write', sales: 'write', invoices: 'write', branches: 'read', customers: 'write', suppliers: 'write', supplier_payments: 'write', expenses: 'write', cash_transfers: 'write', employees: 'write', documents: 'write', leave: 'write', loans: 'read', shifts: 'write', stock: 'write', kitchen: 'write', pos: 'write', reports: 'write', credit_report: 'write', supplier_report: 'write', analytics: 'write', settings: 'read', users: 'none', partners: 'none', fines: 'read' },
  },
  {
    id: 'accountant',
    label: 'Accountant',
    icon: Calculator,
    color: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-50 border-violet-200 hover:border-violet-400',
    description: 'Finance & accounting',
    role: 'manager',
    allowed_modules: ['accounting', 'reports'],
    permissions: { dashboard: 'write', sales: 'read', invoices: 'write', branches: 'read', customers: 'read', suppliers: 'write', supplier_payments: 'write', expenses: 'write', cash_transfers: 'write', employees: 'read', documents: 'read', leave: 'none', loans: 'read', shifts: 'none', stock: 'read', kitchen: 'none', pos: 'none', reports: 'write', credit_report: 'write', supplier_report: 'write', analytics: 'write', settings: 'read', users: 'none', partners: 'read', fines: 'write' },
  },
  {
    id: 'hr_manager',
    label: 'HR Manager',
    icon: UsersIcon,
    color: 'from-emerald-500 to-green-600',
    bg: 'bg-success/10 border-success/30 hover:border-emerald-400',
    description: 'Employee & workforce',
    role: 'manager',
    allowed_modules: ['hr'],
    permissions: { dashboard: 'write', sales: 'none', invoices: 'none', branches: 'read', customers: 'none', suppliers: 'none', supplier_payments: 'none', expenses: 'read', cash_transfers: 'none', employees: 'write', documents: 'write', leave: 'write', loans: 'write', shifts: 'write', stock: 'none', kitchen: 'none', pos: 'none', reports: 'read', credit_report: 'none', supplier_report: 'none', analytics: 'read', settings: 'none', users: 'none', partners: 'none', fines: 'none' },
  },
  {
    id: 'stock_manager',
    label: 'Stock Manager',
    icon: Package,
    color: 'from-orange-500 to-red-500',
    bg: 'bg-orange-50 border-orange-200 hover:border-orange-400',
    description: 'Inventory & procurement',
    role: 'operator',
    allowed_modules: ['pos'],
    permissions: { dashboard: 'none', sales: 'read', invoices: 'none', branches: 'read', customers: 'none', suppliers: 'write', supplier_payments: 'read', expenses: 'none', cash_transfers: 'none', employees: 'none', documents: 'none', leave: 'none', loans: 'none', shifts: 'none', stock: 'write', kitchen: 'write', pos: 'none', reports: 'read', credit_report: 'none', supplier_report: 'read', analytics: 'none', settings: 'none', users: 'none', partners: 'none', fines: 'none' },
  },
  {
    id: 'viewer',
    label: 'Viewer / Auditor',
    icon: BookOpen,
    color: 'from-stone-500 to-stone-700',
    bg: 'bg-muted/50 border-border hover:border-stone-400',
    description: 'Read-only access to all',
    role: 'operator',
    allowed_modules: ['accounting', 'hr', 'pos', 'reports'],
    permissions: { dashboard: 'read', sales: 'read', invoices: 'read', branches: 'read', customers: 'read', suppliers: 'read', supplier_payments: 'read', expenses: 'read', cash_transfers: 'read', employees: 'read', documents: 'read', leave: 'read', loans: 'read', shifts: 'read', stock: 'read', kitchen: 'read', pos: 'read', reports: 'read', credit_report: 'read', supplier_report: 'read', analytics: 'read', settings: 'none', users: 'none', partners: 'read', fines: 'read' },
  },
];

// Map icon strings to Lucide components
const ICON_MAP = {
  ShoppingCart, UtensilsCrossed, Building2, Calculator, Users: UsersIcon,
  Package, BookOpen, Shield, Briefcase, Wrench, HeartPulse, Truck,
  Monitor, Star, Coffee, Scissors, Camera, Palette, Headphones, Settings2,
};

const ICON_OPTIONS = Object.keys(ICON_MAP);

const COLOR_OPTIONS = [
  { value: 'from-amber-500 to-orange-600', label: 'Amber' },
  { value: 'from-teal-500 to-emerald-600', label: 'Teal' },
  { value: 'from-blue-500 to-indigo-600', label: 'Blue' },
  { value: 'from-violet-500 to-purple-600', label: 'Violet' },
  { value: 'from-emerald-500 to-green-600', label: 'Green' },
  { value: 'from-orange-500 to-red-500', label: 'Orange' },
  { value: 'from-rose-500 to-pink-600', label: 'Rose' },
  { value: 'from-cyan-500 to-sky-600', label: 'Cyan' },
  { value: 'from-fuchsia-500 to-purple-600', label: 'Fuchsia' },
  { value: 'from-lime-500 to-green-600', label: 'Lime' },
];

// Convert old list format to new dict format
function normalizePermissions(perms) {
  if (Array.isArray(perms)) {
    const dict = {};
    perms.forEach(p => { dict[p] = 'write'; });
    return dict;
  }
  if (typeof perms === 'object' && perms !== null) {
    return perms;
  }
  return {};
}

// Get default permissions for a role
function getDefaultPermissions(role) {
  const defaults = {};
  ALL_MODULES.forEach(m => {
    if (role === 'admin') {
      defaults[m.value] = 'write';
    } else if (role === 'manager') {
      // Managers get write access to most modules except admin-only ones
      if (['users', 'settings', 'partners'].includes(m.value)) {
        defaults[m.value] = 'read';
      } else {
        defaults[m.value] = 'write';
      }
    } else {
      // Operators get basic access - NO dashboard by default
      if (['sales', 'expenses', 'customers', 'pos'].includes(m.value)) {
        defaults[m.value] = 'write';
      } else {
        defaults[m.value] = 'none';
      }
    }
  });
  return defaults;
}

export default function UsersPage() {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const { branches, fetchBranches: _fetchBr } = useBranchStore();
  const currentUser = useAuthStore((s) => s.user);
  const isCurrentAdmin = currentUser?.role === 'admin' || currentUser?.is_super_admin;
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeEditTab, setActiveEditTab] = useState('details');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'operator',
    branch_id: '',
    allowed_branches: [],
    permissions: {},
    allowed_modules: [],
    must_change_password: false
  });
  
  // Password reset dialog state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  // Activity drawer state
  const [activityUser, setActivityUser] = useState(null);
  // iter822 — "My Access" summary dialog
  const [accessUser, setAccessUser] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const applyingTemplateRef = useRef(false);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [usageStats, setUsageStats] = useState({});
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '', description: '', icon: 'Star', color: 'from-blue-500 to-indigo-600',
    role: 'operator', permissions: {}, allowed_modules: [],
  });
  const [resetData, setResetData] = useState({
    new_password: '',
    confirm_password: '',
    must_change_on_login: true
  });

  // Bulk test-user cleanup state
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupSelected, setCleanupSelected] = useState(new Set());

  const openCleanupDialog = async () => {
    setShowCleanupDialog(true);
    setCleanupLoading(true);
    setCleanupPreview(null);
    try {
      const res = await api.get('/users/test-accounts/preview');
      setCleanupPreview(res.data);
      setCleanupSelected(new Set((res.data?.users || []).map(u => u.id)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load preview');
    } finally {
      setCleanupLoading(false);
    }
  };

  const toggleCleanupRow = (id) => {
    setCleanupSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const confirmBulkCleanup = async () => {
    const ids = Array.from(cleanupSelected);
    if (ids.length === 0) { toast.error('Select at least one user'); return; }
    if (!window.confirm(`Permanently delete ${ids.length} user account(s)? This cannot be undone.`)) return;
    setCleanupLoading(true);
    try {
      const res = await api.post('/users/test-accounts/bulk-delete', { user_ids: ids });
      toast.success(`Deleted ${res.data.deleted} user(s). Skipped: ${res.data.skipped?.length || 0}`);
      setShowCleanupDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setCleanupLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes] = await Promise.all([
        api.get('/users'),
      ]);
      setUsers(usersRes.data);
      fetchCustomTemplates();
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomTemplates = async () => {
    try {
      const [res, statsRes] = await Promise.all([
        api.get('/role-templates'),
        api.get('/template-usage/stats'),
      ]);
      setCustomTemplates(res.data || []);
      const statsMap = {};
      (statsRes.data || []).forEach(s => { statsMap[s.template_id] = s; });
      setUsageStats(statsMap);
    } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare data - ensure permissions is a dict
      const submitData = {
        ...formData,
        permissions: formData.permissions || {},
        allowed_branches: formData.allowed_branches || [],
        // Allow empty branch_id (means "All Branches" access)
        branch_id: formData.branch_id || '',
      };
      
      if (editingUser) {
        const updateData = { ...submitData };
        delete updateData.password;
        delete updateData.email;
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully');
      } else {
        await api.post('/users', submitData);
        toast.success('User created successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setActiveEditTab('details');
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      branch_id: user.branch_id || '',
      allowed_branches: user.allowed_branches || [],
      permissions: normalizePermissions(user.permissions),
      allowed_modules: user.allowed_modules || [],
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${id}`);
        toast.success('User deleted successfully');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  const handleRoleChange = (newRole) => {
    if (applyingTemplateRef.current) return;
    setFormData(prev => {
      if (prev.role === newRole) return prev;
      return { ...prev, role: newRole, permissions: getDefaultPermissions(newRole) };
    });
    setSelectedTemplate(null);
  };

  const applyTemplate = (template) => {
    applyingTemplateRef.current = true;
    setSelectedTemplate(template.id);
    setFormData(prev => ({
      ...prev,
      role: template.role,
      permissions: { ...template.permissions },
      allowed_modules: [...(template.allowed_modules || [])],
    }));
    // Track usage analytics
    api.post('/template-usage/track', {
      template_id: template.id,
      template_name: template.label || template.name,
    }).then(() => {
      setUsageStats(prev => ({
        ...prev,
        [template.id]: {
          ...prev[template.id],
          template_id: template.id,
          usage_count: (prev[template.id]?.usage_count || 0) + 1,
          last_used_at: new Date().toISOString(),
        },
      }));
    }).catch(() => {});
    setTimeout(() => {
      applyingTemplateRef.current = false;
      toast.success(`Applied "${template.label || template.name}" template`);
    }, 100);
  };

  const resetTemplateForm = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '', description: '', icon: 'Star', color: 'from-blue-500 to-indigo-600',
      role: 'operator', permissions: getDefaultPermissions('operator'), allowed_modules: [],
    });
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    try {
      if (editingTemplate) {
        await api.put(`/role-templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/role-templates', templateForm);
        toast.success('Template created');
      }
      fetchCustomTemplates();
      resetTemplateForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this custom template?')) return;
    try {
      await api.delete(`/role-templates/${id}`);
      toast.success('Template deleted');
      fetchCustomTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const startEditTemplate = (tmpl) => {
    setEditingTemplate(tmpl);
    setTemplateForm({
      name: tmpl.name || '',
      description: tmpl.description || '',
      icon: tmpl.icon || 'Star',
      color: tmpl.color || 'from-blue-500 to-indigo-600',
      role: tmpl.role || 'operator',
      permissions: tmpl.permissions || {},
      allowed_modules: tmpl.allowed_modules || [],
    });
  };

  const setPermissionLevel = (module, level) => {
    if (applyingTemplateRef.current) return;
    setFormData(prev => {
      if ((prev.permissions || {})[module] === level) return prev;
      return { ...prev, permissions: { ...prev.permissions, [module]: level } };
    });
  };

  const setAllPermissions = (level) => {
    applyingTemplateRef.current = true;
    const newPerms = {};
    ALL_MODULES.forEach(m => { newPerms[m.value] = level; });
    setFormData(prev => ({ ...prev, permissions: newPerms }));
    requestAnimationFrame(() => { applyingTemplateRef.current = false; });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'operator',
      branch_id: '',
      allowed_branches: [],
      permissions: getDefaultPermissions('operator'),
      allowed_modules: ['accounting', 'hr', 'pos', 'reports', 'admin'],
      must_change_password: false
    });
    setEditingUser(null);
    setSelectedTemplate(null);
  };

  // Password reset functions
  const openResetDialog = (user) => {
    setResetUser(user);
    setResetData({ new_password: '', confirm_password: '', must_change_on_login: true });
    setShowResetDialog(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetData.new_password !== resetData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (resetData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.put(`/users/${resetUser.id}/reset-password`, {
        new_password: resetData.new_password,
        must_change_on_login: resetData.must_change_on_login
      });
      toast.success(`Password reset for ${resetUser.name}`);
      setShowResetDialog(false);
      setResetUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-primary/20 text-primary border-primary/30';
      case 'manager':
        return 'bg-info/20 text-info border-info/30';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  };

  const getPermissionSummary = (permissions) => {
    const perms = normalizePermissions(permissions);
    const write = Object.values(perms).filter(v => v === 'write').length;
    const read = Object.values(perms).filter(v => v === 'read').length;
    return { write, read, total: ALL_MODULES.length };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shadow-orange-200/50">
                <Shield size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="users-page-title">User Management</h1>
                <p className="text-sm text-muted-foreground mt-0.5">Manage user accounts, roles & permissions</p>
              </div>
            </div>
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            <PageTour storageKey="tour_users" steps={[
              { target: '[data-testid="add-user-button"]', title: 'Add User', content: 'Create user accounts with specific roles, branch access, and granular permissions.' },
              { target: '[data-testid="manage-templates-button"]', title: 'Role Templates', content: 'Create reusable permission templates for common roles like Manager, Cashier, etc.' },
              { target: '[data-testid="user-search-input"]', title: 'Search & Filter', content: 'Quickly find users by name or email. Filter by branch to see specific teams.' },
            ]} />
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-1.5 text-sm rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 w-48"
                data-testid="user-search-input-2"
              />
            </div>
            <BranchFilter onChange={setBranchFilter} />
            {isCurrentAdmin && (
              <Button variant="outline" className="rounded-full" onClick={() => { resetTemplateForm(); setShowTemplateManager(true); }} data-testid="manage-templates-button-2">
                <Settings2 size={16} className="mr-2" />
                Manage Templates
              </Button>
            )}
            {isCurrentAdmin && (
              <Button
                variant="outline"
                className="rounded-full border-error/40 text-error hover:bg-error/10"
                onClick={openCleanupDialog}
                data-testid="cleanup-test-users-button"
              >
                <Trash2 size={16} className="mr-2" />
                Clean Test Users
              </Button>
            )}
            <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="add-user-button-2">
                  <Plus size={18} className="mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto" data-testid="user-dialog" aria-describedby="user-dialog-description">
                <DialogHeader>
                  <DialogTitle className="font-outfit">{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                </DialogHeader>
                {/* iter869 — Tab-style sub-nav shown only when editing an existing user */}
                {editingUser && (
                  <div className="flex gap-1 border-b -mt-1 mb-4" data-testid="user-edit-tabs">
                    <button
                      type="button"
                      onClick={() => setActiveEditTab('details')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeEditTab === 'details' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                      data-testid="tab-details"
                    >Details</button>
                    <button
                      type="button"
                      onClick={() => setActiveEditTab('reports')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeEditTab === 'reports' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                      data-testid="tab-reports"
                    >Reports &amp; Alerts</button>
                  </div>
                )}
                <div className={editingUser && activeEditTab !== 'details' ? 'hidden' : ''}>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Name *</Label>
                      <Input
                        value={formData.name}
                        data-testid="user-name-input"
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        data-testid="user-email-input"
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        disabled={editingUser}
                      />
                    </div>
                  </div>

                  {!editingUser && (
                    <div>
                      <Label>Password *</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        data-testid="user-password-input"
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required={!editingUser}
                        minLength={6}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Role *</Label>
                      <Select value={formData.role} onValueChange={handleRoleChange}>
                        <SelectTrigger data-testid="user-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin (Full Access)</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="operator">Operator</SelectItem>
                          <SelectItem value="chef">Chef (Kitchen/KDS)</SelectItem>
                          <SelectItem value="tailor">Tailor (Workshop Orders)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Assigned Branches</Label>
                      <div className="border rounded-md p-2 max-h-32 overflow-y-auto bg-background space-y-1" data-testid="user-branches-multi">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox"
                            checked={(!formData.allowed_branches || formData.allowed_branches.length === 0)}
                            onChange={(e) => {
                              if (e.target.checked) setFormData({ ...formData, branch_id: '', allowed_branches: [] });
                            }}
                            className="rounded" data-testid="user-branch-all" />
                          <span className="font-medium">All Branches (Full Access)</span>
                        </label>
                        {branches.map((branch) => {
                          const checked = (formData.allowed_branches || []).includes(branch.id);
                          return (
                            <label key={branch.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/40 px-1 rounded">
                              <input type="checkbox" checked={checked} onChange={(e) => {
                                const cur = new Set(formData.allowed_branches || []);
                                if (e.target.checked) cur.add(branch.id); else cur.delete(branch.id);
                                const arr = Array.from(cur);
                                setFormData({ ...formData, allowed_branches: arr, branch_id: arr.length === 1 ? arr[0] : '' });
                              }} className="rounded" data-testid={`user-branch-${branch.id}`} />
                              <span>{branch.name}</span>
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {(formData.allowed_branches || []).length === 0
                          ? 'User can access data from all branches'
                          : `User will only see data from ${formData.allowed_branches.length} selected branch(es) — sales, expenses, claims, etc.`}
                      </p>
                    </div>
                  </div>

                  {/* Role Templates - Quick Apply */}
                  {!editingUser && (() => {
                    // iter826 — Settings → Role Management is the SOURCE OF TRUTH.
                    // Server templates with the same name override the built-in
                    // quick-template's permissions (and are removed from the
                    // "custom" list to avoid duplicates).
                    const byName = {};
                    customTemplates.forEach(t => { byName[(t.name || '').trim().toLowerCase()] = t; });
                    const mergedBuiltins = ROLE_TEMPLATES.map(t => {
                      const server = byName[t.label.toLowerCase()] || byName[t.id.toLowerCase()];
                      if (!server) return t;
                      return {
                        ...t,
                        permissions: server.permissions || t.permissions,
                        allowed_modules: server.allowed_modules || t.allowed_modules,
                        description: server.description || t.description,
                        synced: true,
                      };
                    });
                    const builtinNames = new Set(ROLE_TEMPLATES.flatMap(t => [t.label.toLowerCase(), t.id.toLowerCase()]));
                    const extraCustoms = customTemplates.filter(t => !builtinNames.has((t.name || '').trim().toLowerCase()));
                    // Find the most popular template
                    const allTmpls = [...mergedBuiltins.map(t => t.id), ...extraCustoms.map(t => t.id)];
                    let maxUsage = 0;
                    let mostPopularId = null;
                    allTmpls.forEach(id => {
                      const count = usageStats[id]?.usage_count || 0;
                      if (count > maxUsage) { maxUsage = count; mostPopularId = id; }
                    });
                    return (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Zap size={16} className="text-warning" />
                        <Label className="text-base font-semibold">Quick Templates</Label>
                        <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Click to apply</span>
                        <span className="text-[10px] text-muted-foreground">Synced with Settings → Roles & Permissions</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="role-templates-grid">
                        {mergedBuiltins.map((tmpl) => {
                          const Icon = tmpl.icon;
                          const isActive = selectedTemplate === tmpl.id;
                          const count = usageStats[tmpl.id]?.usage_count || 0;
                          const isMostPopular = mostPopularId === tmpl.id && maxUsage > 0;
                          return (
                            <button
                              key={tmpl.id}
                              type="button"
                              onClick={() => applyTemplate(tmpl)}
                              data-testid={`template-${tmpl.id}`}
                              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center relative ${
                                isActive
                                  ? 'border-primary bg-primary/5 ring-2 ring-primary/20 scale-[1.02]'
                                  : `${tmpl.bg} border-transparent`
                              }`}
                            >
                              {isMostPopular && (
                                <span className="absolute -top-1.5 -right-1.5 flex items-center gap-0.5 text-[8px] bg-gradient-to-r from-orange-500 to-red-500 text-white px-1.5 py-0.5 rounded-full font-bold shadow-sm" data-testid="most-popular-badge">
                                  <Flame size={8} /> Popular
                                </span>
                              )}
                              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${tmpl.color} flex items-center justify-center`}>
                                <Icon size={14} className="text-white" />
                              </div>
                              <span className="text-xs font-semibold text-foreground">{tmpl.label}</span>
                              <span className="text-[10px] text-muted-foreground leading-tight">{tmpl.description}</span>
                              {count > 0 && (
                                <span className="text-[9px] text-muted-foreground flex items-center gap-0.5" data-testid={`usage-count-${tmpl.id}`}>
                                  <TrendingUp size={8} /> {count}x used
                                </span>
                              )}
                            </button>
                          );
                        })}
                        {extraCustoms.map((tmpl) => {
                          const Icon = ICON_MAP[tmpl.icon] || Star;
                          const isActive = selectedTemplate === tmpl.id;
                          const count = usageStats[tmpl.id]?.usage_count || 0;
                          const isMostPopular = mostPopularId === tmpl.id && maxUsage > 0;
                          return (
                            <button
                              key={tmpl.id}
                              type="button"
                              onClick={() => applyTemplate({ ...tmpl, label: tmpl.name })}
                              data-testid={`template-custom-${tmpl.id}`}
                              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center relative ${
                                isActive
                                  ? 'border-primary bg-primary/5 ring-2 ring-primary/20 scale-[1.02]'
                                  : 'bg-white border-border hover:border-stone-400'
                              }`}
                            >
                              {isMostPopular ? (
                                <span className="absolute -top-1.5 -right-1.5 flex items-center gap-0.5 text-[8px] bg-gradient-to-r from-orange-500 to-red-500 text-white px-1.5 py-0.5 rounded-full font-bold shadow-sm" data-testid="most-popular-badge-2">
                                  <Flame size={8} /> Popular
                                </span>
                              ) : (
                                <span className="absolute top-1 right-1 text-[8px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-semibold">Custom</span>
                              )}
                              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${tmpl.color || 'from-blue-500 to-indigo-600'} flex items-center justify-center`}>
                                <Icon size={14} className="text-white" />
                              </div>
                              <span className="text-xs font-semibold text-foreground">{tmpl.name}</span>
                              <span className="text-[10px] text-muted-foreground leading-tight">{tmpl.description}</span>
                              {count > 0 && (
                                <span className="text-[9px] text-muted-foreground flex items-center gap-0.5" data-testid={`usage-count-${tmpl.id}`}>
                                  <TrendingUp size={8} /> {count}x used
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })()}

                  {formData.role !== 'admin' && (
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <Label className="text-base font-semibold">Module Permissions</Label>
                        <div className="flex gap-2">
                          <Button type="button" size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => setAllPermissions('write')}>
                            <Pencil size={12} /> All Write
                          </Button>
                          <Button type="button" size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => setAllPermissions('read')}>
                            <Eye size={12} /> All Read
                          </Button>
                          <Button type="button" size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => setAllPermissions('none')}>
                            <EyeOff size={12} /> All None
                          </Button>
                        </div>
                      </div>
                      <div className="border rounded-xl p-4 bg-muted/50 dark:bg-foreground space-y-4 max-h-72 overflow-y-auto">
                        {PERMISSION_GROUPS.map(group => (
                          <div key={group}>
                            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">{group}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {ALL_MODULES.filter(m => m.group === group).map((module) => {
                                const currentLevel = formData.permissions[module.value] || 'none';
                                return (
                                  <div key={module.value} className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-muted border border-border dark:border-stone-700">
                                    <span className="text-sm font-medium">{module.label}</span>
                                    <div className="flex rounded-lg overflow-hidden border border-border" data-testid={`permission-${module.value}`}>
                                      {[
                                        { val: 'write', label: 'W', cls: 'bg-success text-white', title: 'Write' },
                                        { val: 'read', label: 'R', cls: 'bg-info text-white', title: 'Read' },
                                        { val: 'none', label: '—', cls: 'bg-stone-400 text-white', title: 'None' },
                                      ].map(opt => (
                                        <button
                                          key={opt.val}
                                          type="button"
                                          title={opt.title}
                                          onClick={() => setPermissionLevel(module.value, opt.val)}
                                          className={`w-7 h-7 text-[10px] font-bold transition-all ${
                                            currentLevel === opt.val
                                              ? opt.cls
                                              : 'bg-white text-muted-foreground hover:bg-muted/50'
                                          }`}
                                        >
                                          {opt.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        <strong>Write:</strong> Can view and modify • <strong>Read:</strong> Can only view • <strong>None:</strong> No access
                      </p>
                    </div>
                  )}

                  {formData.role === 'admin' && (
                    <div className="p-4 bg-success/10 dark:bg-green-900/20 rounded-xl border border-success/30 dark:border-green-700">
                      <p className="text-sm text-success dark:text-green-300 flex items-center gap-2">
                        <Shield size={16} /> Admins have full access to all modules. No permission configuration needed.
                      </p>
                    </div>
                  )}

                  {/* Module Access Control */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Layers size={16} className="text-muted-foreground" />
                      <Label className="text-base font-semibold">Module Access</Label>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">Control which application modules this user can access from the App Launcher.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {APP_MODULES.map((mod) => {
                        const isEnabled = (formData.allowed_modules || []).includes(mod.id);
                        return (
                          <div
                            key={mod.id}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                              isEnabled
                                ? `${mod.color} border-current/20`
                                : 'bg-muted/50 text-muted-foreground border-border'
                            }`}
                            onClick={() => {
                              setFormData(prev => {
                                const current = prev.allowed_modules || [];
                                const updated = current.includes(mod.id)
                                  ? current.filter(m => m !== mod.id)
                                  : [...current, mod.id];
                                return { ...prev, allowed_modules: updated };
                              });
                            }}
                            data-testid={`module-access-${mod.id}`}
                          >
                            <span className="text-sm font-medium">{mod.label}</span>
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                              isEnabled ? 'bg-primary border-primary' : 'bg-white border-border'
                            }`}>
                              {isEnabled && <span className="text-white text-xs font-bold">&#10003;</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="submit" data-testid="submit-user-button" className="rounded-full">
                      {editingUser ? 'Update' : 'Create'} User
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="rounded-full">
                      Cancel
                    </Button>
                  </div>
                </form>
                </div>
                {editingUser && (
                  <div className={activeEditTab !== 'reports' ? 'hidden' : ''} data-testid="reports-tab-panel">
                    <UserReportPreferences userId={editingUser.id} />
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card className="border-0 shadow-sm bg-white dark:bg-muted">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-outfit text-sm font-semibold">All Users</CardTitle>
              <Badge variant="outline" className="text-xs font-normal">{users.length} total</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Name</th>
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Email</th>
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Role</th>
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Branch</th>
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Permissions</th>
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Modules</th>
                    <th className="text-right p-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => {
                    if (searchQuery) {
                      const q = searchQuery.toLowerCase();
                      if (!(u.name || '').toLowerCase().includes(q) && !(u.email || '').toLowerCase().includes(q)) return false;
                    }
                    if (branchFilter.length > 0 && !branchFilter.includes(u.branch_id) && !u.branch_id) return false;
                    return true;
                  }).map((user) => {
                    const branchName = branches.find((b) => b.id === user.branch_id)?.name || 'All Branches';
                    const permSummary = getPermissionSummary(user.permissions);
                    return (
                      <tr key={user.id} className="border-b border-border hover:bg-muted/50 transition-colors" data-testid="user-row">
                        <td className="p-3 text-sm font-semibold text-foreground">{user.name}</td>
                        <td className="p-3 text-sm text-muted-foreground">{user.email}</td>
                        <td className="p-3">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${getRoleBadgeClass(user.role)}`}>
                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </span>
                        </td>
                        <td className="p-3 text-sm">{branchName}</td>
                        <td className="p-3 text-sm">
                          {user.role === 'admin' ? (
                            <Badge className="bg-success/20 text-success border-success/30">Full Access</Badge>
                          ) : (
                            <div className="flex gap-1 items-center flex-wrap">
                              <Badge className="bg-success/20 text-success border-success/30">{permSummary.write} write</Badge>
                              <Badge className="bg-info/20 text-info border-info/30">{permSummary.read} read</Badge>
                              {user.must_change_password && (
                                <Badge className="bg-warning/20 text-warning border-warning/30 flex items-center gap-1">
                                  <AlertTriangle size={10} /> Must Change PW
                                </Badge>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-sm">
                          <div className="flex gap-1 flex-wrap max-w-[150px]">
                            {(() => {
                              const mods = user.allowed_modules || ['accounting', 'hr', 'pos', 'reports', 'admin'];
                              if (mods.length >= APP_MODULES.length) {
                                return <Badge className="bg-success/20 text-success border-success/30">All Modules</Badge>;
                              }
                              return mods.map(m => (
                                <Badge key={m} className="bg-muted text-muted-foreground border-border text-[10px]">
                                  {m === 'hr' ? 'HR' : m.charAt(0).toUpperCase() + m.slice(1)}
                                </Badge>
                              ));
                            })()}
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openResetDialog(user)}
                              data-testid="reset-password-button"
                              className="h-8"
                              title="Reset Password"
                            >
                              <Key size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setAccessUser(user)}
                              data-testid={`view-access-${user.id}`}
                              className="h-8"
                              title="Access Summary"
                            >
                              <Shield size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setActivityUser(user)}
                              data-testid={`view-activity-${user.id}`}
                              className="h-8"
                              title="View Activity"
                            >
                              <Activity size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(user)}
                              data-testid="edit-user-button"
                              className="h-8"
                            >
                              <Edit size={14} className="mr-1" />
                              Edit
                            </Button>
                            {isCurrentAdmin && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDelete(user.id)}
                                data-testid="delete-user-button"
                                className="h-8 text-error hover:text-error"
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Password Reset Dialog */}
        <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <DialogContent className="max-w-md" data-testid="reset-password-dialog" aria-describedby="reset-password-description">
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2">
                <Key size={20} /> Reset Password
              </DialogTitle>
            </DialogHeader>
            {resetUser && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="p-3 bg-muted dark:bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Resetting password for:</p>
                  <p className="font-medium">{resetUser.name}</p>
                  <p className="text-sm text-muted-foreground">{resetUser.email}</p>
                </div>

                <div>
                  <Label>New Password *</Label>
                  <Input
                    type="password"
                    value={resetData.new_password}
                    onChange={(e) => setResetData({ ...resetData, new_password: e.target.value })}
                    data-testid="reset-new-password"
                    placeholder="Enter new password (min 6 characters)"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <Label>Confirm Password *</Label>
                  <Input
                    type="password"
                    value={resetData.confirm_password}
                    onChange={(e) => setResetData({ ...resetData, confirm_password: e.target.value })}
                    data-testid="reset-confirm-password"
                    placeholder="Confirm new password"
                    required
                    minLength={6}
                  />
                </div>

                <div className="flex items-center gap-3 p-3 bg-warning/10 dark:bg-amber-900/20 rounded-lg border border-warning/30 dark:border-amber-800">
                  <Checkbox
                    id="must-change"
                    checked={resetData.must_change_on_login}
                    onCheckedChange={(checked) => setResetData({ ...resetData, must_change_on_login: checked })}
                    data-testid="must-change-checkbox"
                  />
                  <label htmlFor="must-change" className="text-sm cursor-pointer">
                    <span className="font-medium">Force password change on next login</span>
                    <p className="text-xs text-muted-foreground">User will be required to set a new password after logging in</p>
                  </label>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="submit" data-testid="submit-reset-button" className="rounded-full">
                    Reset Password
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowResetDialog(false)} className="rounded-full">
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Access Summary Dialog */}
      {accessUser && (
        <UserAccessSummaryDialog
          user={accessUser}
          branches={branches}
          allModules={ALL_MODULES}
          appModules={APP_MODULES}
          onClose={() => setAccessUser(null)}
        />
      )}

      {/* Activity Drawer Dialog */}
      <Dialog open={!!activityUser} onOpenChange={(o) => { if (!o) setActivityUser(null); }}>
        <DialogContent className="max-w-3xl max-h-[85dvh] overflow-y-auto" data-testid="activity-drawer-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Activity size={18} className="text-violet-500" />
              Activity — {activityUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-muted-foreground mb-2">
            {activityUser?.email} · {activityUser?.role}
          </div>
          {activityUser && (
            <ActivityFeedPanel
              key={activityUser.id}
              scope="user"
              userId={activityUser.id}
              limit={200}
              title="Recent activity"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Template Manager Dialog */}
      <TemplateManagerDialog
        open={showTemplateManager}
        onOpenChange={setShowTemplateManager}
        customTemplates={customTemplates}
        usageStats={usageStats}
        editingTemplate={editingTemplate}
        templateForm={templateForm}
        setTemplateForm={setTemplateForm}
        onSave={handleSaveTemplate}
        onDelete={handleDeleteTemplate}
        onStartEdit={startEditTemplate}
        onResetForm={resetTemplateForm}
        ICON_MAP={ICON_MAP}
        ICON_OPTIONS={ICON_OPTIONS}
        COLOR_OPTIONS={COLOR_OPTIONS}
        APP_MODULES={APP_MODULES}
        ALL_MODULES={ALL_MODULES}
        PERMISSION_GROUPS={PERMISSION_GROUPS}
      />

      {/* Bulk Test-User Cleanup Dialog */}
      <Dialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto" data-testid="cleanup-test-users-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <AlertTriangle size={18} className="text-error" />
              Clean Test User Accounts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Matches accounts that look like test/spam (TEST_, @example.com, @test., iter###, demo_, etc.).
              The protected admin <code className="px-1 bg-muted rounded">ss@ssc.com</code> and your own account are always excluded.
              Review the list below and uncheck anything you want to keep.
            </div>

            {cleanupLoading && !cleanupPreview && (
              <div className="py-6 text-center text-sm text-muted-foreground">Loading preview…</div>
            )}

            {cleanupPreview && (
              <>
                <div className="text-xs text-muted-foreground">
                  Found <span className="font-semibold text-foreground">{cleanupPreview.count}</span> matching account(s).
                  <span className="ml-2">Selected: <span className="font-semibold text-foreground">{cleanupSelected.size}</span></span>
                </div>
                {cleanupPreview.users?.length > 0 ? (
                  <div className="border rounded-lg max-h-80 overflow-y-auto" data-testid="cleanup-test-users-list">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="p-2 text-left w-10">
                              <Checkbox
                                checked={cleanupSelected.size === cleanupPreview.users.length && cleanupPreview.users.length > 0}
                                onCheckedChange={(c) => setCleanupSelected(c ? new Set(cleanupPreview.users.map(u => u.id)) : new Set())}
                                data-testid="cleanup-select-all"
                              />
                            </th>
                            <th className="p-2 text-left">Email</th>
                            <th className="p-2 text-left">Name</th>
                            <th className="p-2 text-left">Role</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cleanupPreview.users.map(u => (
                            <tr key={u.id} className="border-t hover:bg-muted/30" data-testid="cleanup-row">
                              <td className="p-2">
                                <Checkbox
                                  checked={cleanupSelected.has(u.id)}
                                  onCheckedChange={() => toggleCleanupRow(u.id)}
                                  data-testid={`cleanup-check-${u.id}`}
                                />
                              </td>
                              <td className="p-2 font-mono text-xs">{u.email}</td>
                              <td className="p-2">{u.name}</td>
                              <td className="p-2"><Badge variant="outline" className="text-xs">{u.role}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No test-looking accounts found.
                  </div>
                )}
              </>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                variant="destructive"
                className="rounded-full"
                onClick={confirmBulkCleanup}
                disabled={cleanupLoading || !cleanupPreview || cleanupSelected.size === 0}
                data-testid="confirm-cleanup-button"
              >
                <Trash2 size={16} className="mr-2" />
                Delete {cleanupSelected.size > 0 ? `${cleanupSelected.size} ` : ''}Selected
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => setShowCleanupDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
